import type {
  User,
  Credentials,
  PasswordResetTokenIssueParam,
  PasswordResetParam,
} from '@/types/domains/UserType';
import type { PasswordResetTokenIssueExternalParams } from '@/types/externals/obbsApi/users/Params';
import type { RetrieveMaskingEmailExternalResponse } from '@/types/externals/obbsApi/users/Responses';
import type { RegisterParams } from '@/types/externals/obbsApi/auth/register/Params';
import type {
  IssueParams,
  RetrieveEmailResponse,
} from '@/types/externals/obbsApi/auth/emailVerification/Params';
import type { ErrorResponse } from '@/types/externals/obbsApi/commons/Errors';

/**
 * ログインの失敗の文言（応答本文の status ごと）。ここに無い status は「入力に不備があります」
 */
const LOGIN_ERROR_MESSAGES: Partial<Record<ErrorResponse['status'], string>> = {
  423: 'ログインに続けて失敗したため、このメールアドレスでのログインを停止しています。下の「パスワードをお忘れの方はこちら」からパスワードを再設定いただくと、ログインできるようになります。',
  429: '現在、ログインを一時的に制限しています。しばらく時間を置いてから、もう一度お試しください。',
};

export const useAuth = () => {
  const currentUser = useState<User | null>('currentUser', () => null);
  const { checkDailyPopup } = useDailyPopup();

  const sendVerificationEmail = async (params: IssueParams): Promise<void> => {
    sessionStorage.removeItem('verificationEmailErrorMessage');
    const { error } = await useApiFetch<void>('/auth/email-verification/issue', {
      method: 'POST',
      body: params,
    });

    if (error.value) {
      sessionStorage.setItem(
        'verificationEmailErrorMessage',
        'ご入力いただいたメールアドレスは既に登録されています'
      );
    }
  };

  const register = async (token: string, params: RegisterParams): Promise<void> => {
    const { error } = await useApiFetch<void>('/auth/register', {
      method: 'POST',
      body: params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (error.value) {
      throw createError(error.value.title);
    }
    await fetchCurrentUser();
  };

  const retrieveEmail = async (token: string): Promise<string> => {
    const { data, error } = await useApiFetch<RetrieveEmailResponse>(
      'auth/email-verification/retrieve-email',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (error.value) {
      throw createError(error.value.title);
    }

    return String(data.value?.email);
  };

  const fetchCurrentUser = async (): Promise<boolean> => {
    const { data: user, error } = await useApiFetch<User>('/auth/me');

    if (error.value) {
      if (error.value.status === 401) {
        clearCurrentUser();
        return false;
      }
      throw createError(error.value.title);
    }

    currentUser.value = user.value;
    return true;
  };

  /**
   * @returns showsTelContact: ログイン画面に電話の案内を出すか。試行回数の上限でロックされた（423）とき、
   *   同じ IP からの失敗が続いて一時的に止められた（429）ときに出す
   */
  const login = async (credentials: Credentials): Promise<{ showsTelContact: boolean }> => {
    sessionStorage.removeItem('loginErrorMessage');
    const { data: user, error } = await useApiFetch<User>('/auth/authenticate', {
      method: 'POST',
      body: credentials,
    });

    if (error.value) {
      // 423: 試行回数の上限に達してロックされている。再設定への導線はログイン画面の既存リンクを使う
      // 429: 同じ IP からの失敗が続き、一時的に止められている（API 全体の制限に掛かったときも同じ表示）
      const { status } = error.value;
      sessionStorage.setItem(
        'loginErrorMessage',
        LOGIN_ERROR_MESSAGES[status] ?? 'ログインに失敗しました。入力に不備があります。'
      );
      return { showsTelContact: status === 423 || status === 429 };
    }

    if (user.value) {
      currentUser.value = user.value;
      useCart().fetchCartInfo();
      checkDailyPopup(user.value);
      const to = useRoute().redirectedFrom?.path || '/';
      useRouter().push(to);
    }
    return { showsTelContact: false };
  };

  const logout = async (): Promise<void> => {
    const { error } = await useApiFetch('/auth/logout', { method: 'POST' });

    if (error.value) {
      throw createError(error.value.title);
    }

    clearCurrentUser();
    await navigateTo('/login');
  };

  const passwordResetTokenIssue = async (params: PasswordResetTokenIssueParam) => {
    const request: PasswordResetTokenIssueExternalParams = params;
    const { error } = await useApiFetch<void>('/auth/password/issue', {
      method: 'POST',
      body: request,
    });

    if (error.value) {
      throw createError({
        statusCode: error.value.status,
        message: error.value.detail,
        data: error.value.errors,
      });
    }
  };

  const retrieveMaskingEmail = async (token: string) => {
    const { data, error } = await useApiFetch<RetrieveMaskingEmailExternalResponse>(
      '/auth/password/retrieve-email',
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (error.value) {
      throw createError({
        statusCode: error.value.status,
        message: error.value.detail,
        data: error.value.errors,
      });
    }

    return data;
  };

  const resetPassword = async (params: PasswordResetParam): Promise<void> => {
    const { error } = await useApiFetch<void>('/auth/password/update', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${params.token}`,
      },
      body: params,
    });

    if (error.value) {
      throw createError({
        statusCode: error.value.status,
        message: error.value.detail,
        data: error.value.errors,
      });
    }
  };

  const clearCurrentUser = (): void => {
    currentUser.value = null;
  };

  return {
    currentUser: readonly(currentUser),
    sendVerificationEmail,
    register,
    retrieveEmail,
    login,
    logout,
    fetchCurrentUser,
    passwordResetTokenIssue,
    retrieveMaskingEmail,
    resetPassword,
    clearCurrentUser,
  };
};
